"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Checkbox,
  DataTable,
  Notice,
  Panel,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  activeParts,
  analyzeStar,
  buildStarAnswer,
  clock,
  DEFAULT_PACE,
  DEFAULT_TARGET_SECONDS,
  MAX_PACE,
  MIN_PACE,
  type StarAnalysis,
  type StarPart,
  sharesFor,
  spokenWords,
} from "@/lib/tool-kit/research-ops/star";

/** English strings. A translated content file overrides these through `ui`. */
export const STAR_METHOD_ANSWER_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  questionLabel: "The interview question (optional)",
  questionPlaceholder:
    "e.g. Tell me about a time you managed competing deadlines.",
  labelledLabel: "Label each part in the answer",
  learningLabel: "Add a fifth step: what you learned",
  timingHeading: "Timing",
  targetLabel: "Target length (seconds)",
  targetHint: "Interview answers are usually aimed at one to two minutes.",
  paceLabel: "Speaking pace (words per minute)",
  paceHint:
    "Everyday conversation is measured at roughly 165–195 words per minute, but a considered answer is slower. 140 is a steady interview pace; set your own after reading it aloud once.",
  partsHeading: "Your four parts",
  partsHeadingLearning: "Your five parts",
  partFieldLabel: "{n}. {part} — {question}",
  partStats: "{words} of about {budget} words · about {time} spoken",
  wordCount: "one: word|other: words",
  partNames: {
    situation: "Situation",
    task: "Task",
    action: "Action",
    result: "Result",
    learning: "Learning",
  },
  partQuestions: {
    situation: "What was the context?",
    task: "What were you responsible for?",
    action: "What did you do?",
    result: "What happened?",
    learning: "What did you learn?",
  },
  partGuides: {
    situation:
      "Set the scene in two or three sentences: where, when, and what was at stake. Leave out detail the listener does not need.",
    task: "Say what you were responsible for — your goal or the problem you had to solve — in a sentence or two.",
    action:
      "The longest part. Describe the specific steps you took, in order, and why. Say “I” for what you did, even when the work was a team effort.",
    result:
      "Say what happened because of what you did — with a number, a decision, or feedback if you can — and then stop.",
    learning:
      "One or two sentences on what you learned or would do differently. Useful in applications; optional in interviews.",
  },
  planHeading: "Plan and timing",
  planHint:
    "Word budgets follow the University of Washington Career & Internship Services guideline of about 20% Situation, 10% Task, 60% Action and 10% Result; with the Learning step, 10% moves from Action. Other careers services split it a little differently — treat the budget as a guide.",
  colPart: "Part",
  colWords: "Words",
  colBudget: "Budget",
  colTime: "Spoken time",
  total: "Total",
  totalLine: "{words} · {time} at {pace} words per minute · target {target}",
  emptyPrompt: "Write at least one part to see its timing and the checks.",
  checksHeading: "Checks",
  checkLengthOk: "Length: within 10% of your target time.",
  checkLengthOver:
    "Length: about {seconds} seconds over your target. Trim the Situation first.",
  checkLengthUnder:
    "Length: about {seconds} seconds under your target. There is room for one more concrete detail in the Action.",
  checkActionOk: "Action: {percent}% of your words — the right weight.",
  checkActionLow:
    "Action: only {percent}% of your words. The Action is what the interviewer is assessing; give it more steps and fewer setup words.",
  checkActionHigh:
    "Action: {percent}% of your words. Check that the Situation, Task and Result are not squeezed out.",
  checkVoiceOk:
    "Voice: the Action says “I” {singular} times and “we” {plural}.",
  checkVoiceWe:
    "Voice: the Action says “we” {plural} times and “I” {singular}. Say what you personally did, even when the work was a team effort.",
  checkMeasureOk: "Result: it includes a number.",
  checkMeasureMissing:
    "Result: no number yet. If you can, add a count, a percentage, a time saved, or a decision.",
  fileName: "star-answer",
  document: {
    heading: "Your STAR answer",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part is written.",
  },
  defaultTitle: "STAR Answer",
  titleWithQuestion: "STAR Answer: {question}",
  subtitle: "Situation, Task, Action, Result",
  voiceNote:
    "Practise it aloud and cut anything you cannot say in one breath. Remove the part labels before you speak — they are for your notes.",
  partPromptNamed: "{part} — {prompt}",
  timingNote:
    "About {time} spoken at {pace} words per minute (target {target}).",
  example: {
    question: "Tell me about a time you managed competing deadlines.",
    situation:
      "In my final semester I was the only teaching assistant for a first-year chemistry lab of 48 students when the department asked me to cover a colleague's marking too, two weeks before my own thesis draft was due.",
    task: "I had to keep both commitments on track: return lab reports within the usual five days and give my supervisor the thesis draft on time.",
    action:
      "I listed every hour I had that week and found about nine I could free up. I wrote a short marking rubric, tested it on five reports, and shared it with the other teaching assistant, so we marked the same way and I could mark without rereading each report. I checked in with her midweek, found two rubric lines we were reading differently, and rewrote them that evening before the next batch. I batched the marking into two evening blocks and told the students when to expect feedback, which cut the emails asking where their results were. Then I asked my supervisor for a one-week extension on the thesis draft. I explained my workload and offered a specific new date instead of letting the draft slip silently, and we agreed the date in writing. I also kept my lab office hours unchanged, because the first-years depended on them.",
    result:
      "All 48 reports went back within five days, and the rubric cut my marking time per report from about twelve minutes to eight. My supervisor accepted the draft on the new date, and the department adopted the rubric for the next term.",
    learning:
      "I learned to ask for a change of deadline early and in writing, with a specific new date, rather than hoping to catch up.",
  },
} as const;

type Strings = typeof STAR_METHOD_ANSWER_GENERATOR_STRINGS;

const EMPTY_PARTS: Record<StarPart, string> = {
  situation: "",
  task: "",
  action: "",
  result: "",
  learning: "",
};

function clampPace(value: number | null): number {
  if (value === null) {
    return DEFAULT_PACE;
  }
  return Math.min(MAX_PACE, Math.max(MIN_PACE, value));
}

function checkMessage(
  check: StarAnalysis["checks"][number],
  strings: Strings,
  fmt: (value: number) => string
): string {
  const text = {
    lengthOk: strings.checkLengthOk,
    lengthOver: strings.checkLengthOver,
    lengthUnder: strings.checkLengthUnder,
    actionOk: strings.checkActionOk,
    actionLow: strings.checkActionLow,
    actionHigh: strings.checkActionHigh,
    voiceOk: strings.checkVoiceOk,
    voiceWe: strings.checkVoiceWe,
    measureOk: strings.checkMeasureOk,
    measureMissing: strings.checkMeasureMissing,
  }[check.key];
  const values = Object.fromEntries(
    Object.entries(check.values).map(([key, value]) => [key, fmt(value)])
  );
  return t(text, values);
}

export default function StarMethodAnswerGeneratorWidget() {
  const strings = useToolUi(STAR_METHOD_ANSWER_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [question, setQuestion] = useState("");
  const [parts, setParts] = useState<Record<StarPart, string>>(EMPTY_PARTS);
  const [labelled, setLabelled] = useState(true);
  const [withLearning, setWithLearning] = useState(false);
  const [targetText, setTargetText] = useState(String(DEFAULT_TARGET_SECONDS));
  const [paceText, setPaceText] = useState(String(DEFAULT_PACE));

  const targetSeconds = Math.max(
    10,
    parseIntegerInput(targetText) ?? DEFAULT_TARGET_SECONDS
  );
  const pace = clampPace(parseIntegerInput(paceText));
  const order = activeParts(withLearning);
  const analysis = useMemo(
    () => analyzeStar(parts, withLearning, targetSeconds, pace),
    [parts, withLearning, targetSeconds, pace]
  );
  const labels = useMemo(
    () => ({
      ...strings,
      partPrompts: Object.fromEntries(
        order.map((part) => [
          part,
          `${strings.partQuestions[part]} ${strings.partGuides[part]}`,
        ])
      ) as Record<StarPart, string>,
    }),
    [strings, order]
  );
  const doc = useMemo(
    () =>
      buildStarAnswer(
        { question, labelled, withLearning, parts },
        labels,
        { words: analysis.totalWords, pace, targetSeconds },
        clock
      ),
    [
      question,
      labelled,
      withLearning,
      parts,
      labels,
      analysis,
      pace,
      targetSeconds,
    ]
  );
  const shares = sharesFor(withLearning);

  const loadExample = () => {
    setQuestion(strings.example.question);
    setParts({
      situation: strings.example.situation,
      task: strings.example.task,
      action: strings.example.action,
      result: strings.example.result,
      learning: strings.example.learning,
    });
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={loadExample}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setQuestion("");
            setParts(EMPTY_PARTS);
          }}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel>
        <TextField
          id="star-question"
          label={strings.questionLabel}
          onChange={setQuestion}
          placeholder={strings.questionPlaceholder}
          value={question}
        />
        <Checkbox
          checked={labelled}
          label={strings.labelledLabel}
          onChange={setLabelled}
        />
        <Checkbox
          checked={withLearning}
          label={strings.learningLabel}
          onChange={setWithLearning}
        />
      </Panel>

      <Panel title={strings.timingHeading}>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            hint={strings.targetHint}
            id="star-target"
            label={strings.targetLabel}
            numeric
            onChange={setTargetText}
            value={targetText}
          />
          <TextField
            hint={strings.paceHint}
            id="star-pace"
            label={strings.paceLabel}
            numeric
            onChange={setPaceText}
            value={paceText}
          />
        </div>
      </Panel>

      <Panel
        title={
          withLearning ? strings.partsHeadingLearning : strings.partsHeading
        }
      >
        {order.map((part, index) => {
          const words = spokenWords(parts[part]);
          const budget = Math.round(
            ((targetSeconds * pace) / 60) * shares[part]
          );
          return (
            <TextAreaField
              hint={
                <>
                  {strings.partGuides[part]}{" "}
                  <span className="whitespace-nowrap font-medium">
                    {t(strings.partStats, {
                      words: `${fmt(words)}${tPlural(strings.wordCount, locale, words)}`,
                      budget: fmt(budget),
                      time: clock((words / pace) * 60),
                    })}
                  </span>
                </>
              }
              id={`star-${part}`}
              key={part}
              label={t(strings.partFieldLabel, {
                n: index + 1,
                part: strings.partNames[part],
                question: strings.partQuestions[part],
              })}
              onChange={(value) =>
                setParts((previous) => ({ ...previous, [part]: value }))
              }
              prose
              rows={4}
              value={parts[part]}
            />
          );
        })}
      </Panel>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.planHeading}</h2>
        {analysis.totalWords === 0 ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <>
            <DataTable
              head={[
                strings.colPart,
                strings.colWords,
                strings.colBudget,
                strings.colTime,
              ]}
              rows={analysis.parts.map((item) => [
                strings.partNames[item.part],
                fmt(item.words),
                fmt(item.budget),
                clock(item.seconds),
              ])}
            />
            <p className="text-foreground text-sm">
              <strong>{strings.total}:</strong>{" "}
              {t(strings.totalLine, {
                words: `${fmt(analysis.totalWords)}${tPlural(strings.wordCount, locale, analysis.totalWords)}`,
                time: clock(analysis.totalSeconds),
                pace: fmt(pace),
                target: clock(targetSeconds),
              })}
            </p>
            <h3 className="font-semibold text-foreground text-lg">
              {strings.checksHeading}
            </h3>
            <ul className="space-y-2 text-foreground text-sm">
              {analysis.checks.map((check) => (
                <li
                  className={check.ok ? "text-muted-foreground" : "font-medium"}
                  key={check.key}
                >
                  {checkMessage(check, strings, fmt)}
                </li>
              ))}
            </ul>
          </>
        )}
        <p className="text-muted-foreground text-xs">{strings.planHint}</p>
      </section>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="star"
        labels={strings.document}
      />
    </div>
  );
}
